import { clearSignedInStorage } from '../utils/device-preferences';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, of, takeUntil, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { GroupService } from './group.service';
import { environment } from 'src/environments/environment';

import { JwtHelperService } from '@auth0/angular-jwt';
import { SocketService } from './socket.service';
import { ChatPushService } from '../../chat/chat-push.service';
import { redirectToLogin } from '../utils/login-redirect.util';

/** Fallback if `environment.mergeAuthorizedUserId` is missing; must match server default. */
const DEFAULT_MERGE_AUTHORIZED_USER_ID = '511f39e1-ff14-4a6d-b35a-6fcc22923b50';

/**
 * The password was accepted and a token was issued, but loading what the app needs to actually
 * start — the profile, then the user's groups — failed afterwards.
 *
 * It exists purely so the login screen can tell the two failures apart. Both used to surface as
 * "There was an error validating your details. Please check your details and try again", which is
 * a lie in this case and sent users off to re-check a password that was never wrong. On a slow
 * connection this is the common failure, not the rare one: `loginUser` makes three sequential
 * round trips and only the first is about credentials.
 */
export class PostAuthLoadError extends Error {
  // Named `originalError` rather than `cause`, which would shadow the built-in `Error.cause`
  // declared by the ES2022 lib and require an `override` modifier.
  constructor(public readonly originalError: unknown) {
    super('Signed in, but the user profile could not be loaded.');
    this.name = 'PostAuthLoadError';
    // Restores the prototype chain so `instanceof` works after TypeScript's ES5 class downlevel.
    Object.setPrototypeOf(this, PostAuthLoadError.prototype);
  }
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = 'api'; // Replace with your API URL
  private userProfileSubject = new BehaviorSubject<any>(null);
  userProfile$ = this.userProfileSubject.asObservable();

  /**
   * True once a sign-out has begun, so `beforeunload` guards can let the resulting navigation
   * through instead of prompting the user to confirm leaving. Read by
   * `DashboardComponent.setupBeforeUnloadWarning`.
   *
   * Cleared again when a login issues a token. Usually redundant — `logoutAndRedirect()` ends in
   * a new document, and a new document means a new service instance — but `logout()` can also be
   * called WITHOUT leaving the page (see `LoginComponent.showLoginError`, cleaning up after a
   * login that got a token but could not load the profile). Leaving the flag set through the
   * user's next successful login would silently disable the unload warning for the rest of that
   * document's life.
   */
  isSigningOut = false;

  //***************************/
  private isPhysician: boolean = false;
  private canNonPhysicianSign: boolean = false;
  private groupUsers: any[] = [];

  //***************************/

  constructor(
    private http: HttpClient,
    private groupService: GroupService,
    private socketService: SocketService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private jwtHelper: JwtHelperService,
    private injector: Injector,
  ) {
    this.loadUserProfile();

    //*****Sub to group users and check if can sign, set local can sign variable
    this.groupService.groupUsers$
      .subscribe(result => {
        if (result) {
          const thisUser = result.filter((groupUser: any) => groupUser.userId === this.user.id)[0];
          if (thisUser && thisUser.userType == 'doctor') {
            this.isPhysician = true;
          } else {
            this.canNonPhysicianSign = thisUser?.canSignOnDoctorsBehalf; // Set local `canSign` variable
          }
        }

      });
  }

  private loadUserProfile(): void {
    if (isPlatformBrowser(this.platformId)) {
      const token = localStorage.getItem('token');
      if (token) {
        this.getUserProfile()
          .pipe(
            switchMap(() => this.groupService.getUserGroups(this.user.id, this.user.userType))
          ).subscribe();
      }
    }
  }

  get user() {
    return this.userProfileSubject.value;
  }

  /**
   * Uses profile id when loaded, otherwise JWT `userId` so duplicate-merge UI works before /profile resolves.
   */
  get isMergeAuthorizedUser(): boolean {
    const cfg =
      (environment as { mergeAuthorizedUserId?: string }).mergeAuthorizedUserId ??
      DEFAULT_MERGE_AUTHORIZED_USER_ID;
    const uid = this.getCurrentUserIdForMergeAuth();
    if (!uid) {
      return false;
    }
    return String(uid).toLowerCase() === String(cfg).toLowerCase();
  }

  private getCurrentUserIdForMergeAuth(): string | undefined {
    const u = this.user;
    if (u?.id) {
      return String(u.id);
    }
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }
    try {
      const token = localStorage.getItem('token');
      if (!token || this.jwtHelper.isTokenExpired(token)) {
        return undefined;
      }
      const decoded = this.jwtHelper.decodeToken(token) as { userId?: string };
      return decoded?.userId != null ? String(decoded.userId) : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Publishes the in-memory profile. Passing `null` clears it and nothing more.
   *
   * It used to call `logout()` on a null user, which meant the login screen — whose `ngOnInit`
   * is the only caller — wiped `localStorage` every time it was constructed. That is fine when
   * the user arrives to sign in, and a defect at any other moment: if the component was
   * re-created while a login was in flight (a queued `window.location.reload()` finally
   * committing, a bfcache restore replaying the route) it deleted the token the login had just
   * written, and the user landed back on the login page having genuinely signed in. The server
   * had already recorded `login_success`.
   *
   * Signing out is now always explicit — `logout()` or `logoutAndRedirect()` — so this cannot
   * discard a credential as a side effect of a view being built.
   */
  setUserProfile(user: any) {
    this.userProfileSubject.next(user);
  }

  get isUserPhysician() {
    return this.user.userType === 'doctor'; //will return true if user is physician
  }

  get canUserSign(): boolean {
    return this.isPhysician || this.canNonPhysicianSign;
  }

  get isNonPhysician(): boolean {
    return this.canNonPhysicianSign;
  }

  get availableDoctorsList(): any {
    this.groupService.groupUsers$
      .subscribe((users: any[]) => {
        if (users?.length) {
          this.groupUsers = users?.filter(u => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50');
        }
      });
    return this.groupUsers;
  }

  isLoggedIn(): Observable<boolean> {
    if (isPlatformBrowser(this.platformId)) {
      const token = localStorage.getItem('token');
      if (token) {
        // Check if the token is expired
        if (this.jwtHelper.isTokenExpired(token)) {
          // Token is expired
          return of(false);
        } else {
          // Token is valid
          return of(true);
        }
      } else {
        // Token is missing
        return of(false);
      }
    } else {
      // For server-side rendering, assume the user is not logged in
      return of(false);
    }
  }

  validateToken(token?: string): boolean {
    const userToken = token ?? localStorage.getItem('token') ?? undefined;
    return !!(userToken && !this.jwtHelper.isTokenExpired(userToken));
  }

  isEmailVerified(): Observable<boolean> {
    return of(this.user.isVerified);
  }

  resendVerificationEmail(): Observable<void> {
    // Make an API call to resend the verification email
    return this.http.post<void>(`${this.apiUrl}/resend-verification-email`, { userId: this.user.id });
  }

  registerUser(userData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/register`, userData);
  }

  /**
   * Three sequential round trips, and the caller needs to know which one failed.
   *
   * Everything after `POST /login` succeeds is wrapped so its failures arrive as
   * `PostAuthLoadError`. Errors reaching the caller unwrapped are therefore always about the
   * credentials themselves (or the network beneath that first request), which is the only case
   * where "check your details" is honest advice.
   *
   * The previous shape — `map()` returning either an observable or a plain object, then
   * `switchMap(response => response)` — also crashed on the `{ success: false }` 200 branch,
   * because RxJS cannot flatten a plain object. Unreachable today (the server answers bad
   * credentials with a 401) but it is the branch `login.component` still handles, so switching
   * on the response up front removes the trap rather than leaving it armed.
   */
  loginUser(credentials: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/login`, credentials).pipe(
      switchMap((response: any) => {
        if (!response?.success || !response?.token) {
          return of(response);
        }

        localStorage.setItem('token', response.token);
        // A session is starting, so any earlier sign-out in this document is over. See the field.
        this.isSigningOut = false;
        // reauthenticate(), not reconnect(): the socket is already connected (unauthenticated,
        // from bootstrap before login), so it must re-handshake to attach the new token.
        this.socketService.reauthenticate();

        return this.getUserProfile().pipe(
          switchMap(() => this.groupService.getUserGroups(this.user.id, this.user.userType)),
          map(() => response),
          catchError(error => throwError(() => new PostAuthLoadError(error)))
        );
      })
    );
  }

  isAuthenticated(): Observable<boolean> {
    if (isPlatformBrowser(this.platformId)) {
      const token = localStorage.getItem('token');
      if (!token) {
        return of(false);
      }
    }

    // Make an API call to validate the token
    return this.http.get(`${this.apiUrl}/auth/validate-token`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  logout(): void {
    // Set before anything else so the `beforeunload` guards can see a sign-out is under way —
    // see `logoutAndRedirect()` for why that matters. Cleared again by `loginUser` on the next
    // token, for the sign-out paths that do not leave the document.
    this.isSigningOut = true;

    // MUST run before the storage is cleared below — the token is the only credential we have,
    // and clearing it first would leave nothing to authenticate the call with.
    this.recordLogout();

    // Chat notifications stop on this device (M4). Also before the clear: it reads this device's push id from storage.
    // Resolved here, not in the constructor: signing in must not wait on (or construct) chat.
    try {
      this.injector.get(ChatPushService).signOut();
    } catch {
      // chat not in this build or not started: nothing registered
    }

    // NOTE: saved credentials in native secure storage are deliberately NOT
    // cleared here. They persist until app uninstall so the user doesn't have
    // to re-type on every log-out / log-in cycle. Password stays in sync
    // automatically via CredentialPromptService.promptToSaveIfUnsaved when the
    // user next logs in with a changed password.
    // Everything but the per-device preferences (calendar view and the like).
    clearSignedInStorage(localStorage);
    this.socketService.disconnect();
    this.groupService.setSelectedGroup(null);
    this.userProfileSubject.next(null);
  }

  /**
   * Signs out and lands the user on a freshly loaded login page. The only supported way to log
   * out — call this rather than pairing `logout()` with your own navigation.
   *
   * ── Why one hard navigation, not `router.navigate()` then `reload()` ─────────────────────
   *
   * All eight sign-out paths used to do this:
   *
   *     this.userService.logout();
   *     this.router.navigate(['/login']).then(() => { window.location.reload(); });
   *
   * `router.navigate()` resolves as soon as Angular has swapped the router outlet, so the login
   * form is painted and fully interactive BEFORE `reload()` is even called — and the reload then
   * has to fetch `index.html` over the network, which `server-static.js` serves `no-store`. On a
   * fast connection that window is invisible. On a congested mobile link it is seconds, and the
   * user spends it typing into a document that is already condemned: the reload commits and
   * takes the half-finished login with it. That is the "logged in, then thrown straight out"
   * report this replaces. Caching `index.html` in the service worker (`ngsw-config.json`) shrinks
   * the window; only removing the intermediate page closes it.
   *
   * `replace()` rather than `assign()` so the signed-out page does not become a back-button
   * destination.
   *
   * ── The `beforeunload` interaction ───────────────────────────────────────────────────────
   *
   * Routing to `/login` first had one real benefit, easy to miss: it destroyed `DashboardComponent`
   * and so removed its unconditional `beforeunload` handler before the reload. Leaving on a
   * single navigation keeps that component mounted, and without `isSigningOut` the browser would
   * ask "Leave site?" on every log-out. `DashboardComponent.setupBeforeUnloadWarning` reads the
   * flag. The recording guard in `ai-dialog.component` deliberately does NOT, and still blocks.
   */
  logoutAndRedirect(): void {
    this.logout();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    redirectToLogin();
  }

  /**
   * Tells the server this sign-out was deliberate, so the session row can record `endedAt` and be
   * told apart from one the user simply walked away from.
   *
   * ── Why native fetch instead of HttpClient ───────────────────────────────────────────────
   *
   * Every caller of logout() navigates and then calls window.location.reload() immediately after
   * (see header.component, idle.service, auth.guard, screen-wake.service, subscription.service).
   * HttpClient uses XHR here, and the browser aborts in-flight XHR on unload — so the obvious
   * implementation would drop most of the very events it exists to record. `keepalive` is the
   * documented way to say "finish this even though the page is going away", and fetch is the only
   * API that offers it. sendBeacon also survives unload but cannot set an Authorization header,
   * which would force the token into the body or query string, where it would end up in server
   * logs.
   *
   * The cost of leaving HttpClient is that ApiBaseInterceptor does not prefix the URL and
   * JwtInterceptor does not attach the token, so both are done explicitly below.
   *
   * ── Deliberately fire-and-forget ─────────────────────────────────────────────────────────
   *
   * Nothing waits on the response and every failure is swallowed. Refusing to sign a user out
   * because an audit row could not be written would be a far worse defect than the missing row —
   * the same reasoning that keeps authMiddleware and logAuthEvent incapable of failing a request.
   * The consequence is accepted rather than hidden: `endedAt` is best-effort and under-records, so
   * nothing should treat it as an authoritative session-end time.
   *
   * Note this records the sign-out; it does NOT revoke the token, which stays valid until it
   * expires. Revocation is Phase 2b.
   */
  private recordLogout(): void {
    // No localStorage under SSR, and no session to end.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const baseUrl = (environment.API_URL || '').replace(/\/$/, '');
      void fetch(`${baseUrl}/${this.apiUrl}/logout`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: '{}'
      }).catch(() => {
        // Offline, aborted, server down — the user is signed out locally regardless.
      });
    } catch {
      // fetch itself is unavailable (very old WebView). Same outcome: sign out locally.
    }
  }

  getUserProfile(): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap((profile: any) => {
        this.userProfileSubject.next(profile.user);
      })
    );
  }


  updateUserProfile(userData: any): Observable<any> {
    const token = localStorage.getItem('token');

    const formData = new FormData();
    const signatureDataUrl = userData.signature;
    delete userData.signature;
    formData.append('data', JSON.stringify(userData));
    if (signatureDataUrl) {
      const signatureBlob = this.dataURLToBlob(signatureDataUrl);
      formData.append('signature', signatureBlob, 'signature.png');
    }

    // Optimistically update the observable with the data we're sending
    // This ensures subscribers get the update immediately
    const currentUser = this.userProfileSubject.value;
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      this.userProfileSubject.next(updatedUser);
    }

    return this.http.put(`${this.apiUrl}/profile`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap((response: any) => {
        // Update with the actual response from the backend to ensure consistency
        if (response && response.user) {
          this.userProfileSubject.next(response.user);
        } else if (response && response.success) {
          // If response structure is different, merge with current user
          const currentUser = this.userProfileSubject.value;
          if (currentUser) {
            const updatedUser = { ...currentUser, ...userData };
            this.userProfileSubject.next(updatedUser);
          }
        }
      })
    );
  }

  private dataURLToBlob(dataURL: string) {
    const byteString = atob(dataURL.split(",")[1]);
    const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  }

  setLoginCode(loginCode: string, deviceId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/set-login-code`, { loginCode, deviceId }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deleteUser(): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  forgotPassword(email: string, turnstileToken?: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/forgot-password`, { email, turnstileToken });
  }

  resetPassword(resetToken: string, password: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/reset-password`, { resetToken, password });
  }

  verifyEmail(verificationToken: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/verify-email`, { verificationToken });
  }

  calculateStrength(value: string): number {
    let strength = 0;
    if (this.hasLowerCase(value)) strength += 1;
    if (this.hasUpperCase(value)) strength += 1;
    if (this.hasDigit(value)) strength += 1;
    if (this.hasSpecialCharacters(value)) strength += 1;
    if (this.hasMinCharLenght(value, 8)) strength += 1;
    if (this.hasMinCharLenght(value, 12)) strength += 1;
    return (strength / 6) * 100; // Convert to percentage for the progress bar
  }

  //Set the requirements as stand alone modules
  hasLowerCase(inputs: string): boolean {
    return /[a-z]/.test(inputs);
  }

  hasUpperCase(inputs: string): boolean {
    return /[A-Z]/.test(inputs);
  }

  hasDigit(inputs: string): boolean {
    return /[0-9]/.test(inputs);
  }

  hasSpecialCharacters(inputs: string): boolean {
    return /[\W]/.test(inputs);
  }

  hasMinCharLenght(value: string, min: number): boolean {
    return value.length >= min;
  }
}